import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { PageDialog } from '@traceio-org/ui';
import { describeWriteFailure, type WriteFailure } from '../api';

/** What a submit handler answers: 'invalid' keeps the dialog open; the fields already show their errors. */
export type SubmitOutcome = 'invalid' | undefined;

export interface WriteDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** One sentence under the title; longer explanations go in the children. */
  description?: string;
  submitLabel: string;
  /** danger: the submit button says the change is drastic (a stop, a removal, a release). */
  tone?: 'default' | 'danger';
  /** Validates, then sends the change. Throwing reports the failure inside the dialog. */
  onSubmit: () => Promise<SubmitOutcome>;
  /** Called once platform-api accepted the change; the dialog then closes. */
  onSaved: () => void;
  /**
   * Called with a failure the dialog shows. A conflict (409, 412) means the record moved: the page reads it again so
   * a retry is made against what it is now, never against the copy the dialog opened with.
   */
  onFailed?: (failure: WriteFailure) => void;
  /** No submit button: the dialog only explains why the change cannot be made now. */
  submitHidden?: boolean;
  /** The submit button shows but sends nothing: what is filled in would not change anything (aria-disabled). */
  submitDisabled?: boolean;
  /** Label of the closing button (Cancel by default). */
  cancelLabel?: string;
  children: ReactNode;
}

/** The failure of a write, inside its dialog: what happened, platform-api's words, and the support reference. */
export function WriteFailureMessage({ failure }: { failure: WriteFailure }) {
  return (
    <div className="admin-write-failure" role="alert" data-failure={failure.kind}>
      <p className="admin-write-failure__message">{failure.message}</p>
      {failure.detail ? <p>{failure.detail}</p> : null}
      {failure.issues && failure.issues.length > 0 ? (
        <ul>
          {failure.issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : null}
      {failure.reference ? (
        <p className="admin-reference">
          Support reference: <code>{failure.reference}</code>
        </p>
      ) : null}
    </div>
  );
}

/**
 * A change made from a dialog (the library's PageDialog: modal, focus kept inside, Escape closes). It sends
 * once, keeps the dialog open with the failure explained when platform-api refuses or cannot be reached, and
 * closes after platform-api accepted the change. An accepted change whose answer is outside the contract
 * counts as saved: the page reads the current state again rather than claiming it failed.
 */
export function WriteDialog({
  open,
  onClose,
  title,
  description,
  submitLabel,
  tone = 'default',
  onSubmit,
  onSaved,
  onFailed,
  submitHidden = false,
  submitDisabled = false,
  cancelLabel = 'Cancel',
  children,
}: WriteDialogProps) {
  const formId = useId();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  // A dialog closed and opened again starts clean; an answer that arrives after it closed is still handled.
  useEffect(() => {
    if (open) {
      setFailure(null);
      setSubmitting(false);
    }
  }, [open]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || submitHidden || submitDisabled) return;
    setFailure(null);
    setSubmitting(true);
    try {
      const outcome = await onSubmit();
      if (outcome === 'invalid') {
        setSubmitting(false);
        return;
      }
    } catch (error) {
      const described = describeWriteFailure(error);
      if (described.kind !== 'saved-unreadable') {
        setFailure(described);
        setSubmitting(false);
        onFailed?.(described);
        return;
      }
      // platform-api accepted the change (2xx) but its answer did not match the contract.
      console.warn('admin-web: a change was saved but its answer is outside the contract', error);
    }
    setSubmitting(false);
    onSaved();
    onClose();
  };

  const submitClass = `admin-action ${tone === 'danger' ? 'admin-action--danger' : 'admin-action--primary'}`;
  return (
    <PageDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={title}
      {...(description ? { description } : {})}
      footer={
        <div className="admin-dialog-actions">
          <button type="button" className="admin-action" onClick={onClose}>
            {cancelLabel}
          </button>
          {submitHidden ? null : (
            <button
              type="submit"
              form={formId}
              className={submitClass}
              aria-disabled={submitting || submitDisabled || undefined}
              data-busy={submitting || undefined}
            >
              {submitLabel}
            </button>
          )}
        </div>
      }
    >
      <form id={formId} className="admin-dialog-form" onSubmit={(event) => void submit(event)} noValidate>
        {failure ? <WriteFailureMessage failure={failure} /> : null}
        {children}
      </form>
    </PageDialog>
  );
}

/**
 * Which record a dialog is about, and whether it is open. The target stays after closing, so the dialog keeps
 * its content while it animates out and focus returns to the button that opened it; `key` changes on every
 * opening, so each one starts with empty fields.
 */
export function useDialogTarget<T>() {
  const [state, setState] = useState<{ target: T | null; open: boolean; key: number }>({
    target: null,
    open: false,
    key: 0,
  });
  return {
    target: state.target,
    open: state.open,
    key: state.key,
    show: (target: T) => setState((current) => ({ target, open: true, key: current.key + 1 })),
    close: () => setState((current) => ({ ...current, open: false })),
  };
}
